import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * Buy requests (buyer.requests). The server owns limits, matching and the supplier
 * firewall: matches name only the menu owner the buyer already deals with.
 */
export type BuyerRequestMatch = {
  id: string;
  listing_id: string;
  menu_id: string;
  menu_name: string;
  seller_company_id: string;
  seller_company_name: string;
  strain_name: string | null;
  product_form: string | null;
  thc_percent: number | null;
  price_per_g: number | null;
  quantity_g: number | null;
  score: number;
  reasons: string[];
  /** First visible listing photo (storage path or URL). */
  image?: string | null;
  status: 'new' | 'seen' | 'dismissed' | 'contacted';
  matched_at: string;
  /** Grade the buyer asked for, when this listing is the closest grade instead. */
  closest_to?: string | null;
  closest_rank?: number | null;
};

export type BuyerRequest = {
  id: string;
  raw_text: string;
  product_form: string | null;
  strain_query: string | null;
  min_quantity_g: number | null;
  target_price_per_g: number | null;
  max_price_per_g: number | null;
  min_thc_percent: number | null;
  environment: string | null;
  species: string | null;
  needed_by: string | null;
  is_recurring: boolean;
  status: 'active' | 'paused';
  created_at: string;
  last_matched_at: string | null;
  /** Full search-engine criteria the request matches on. */
  parsed_query?: { constraints?: Array<{ field: string; op: string; values?: unknown[]; primary_values?: string[]; source?: string }> } | null;
  matches: BuyerRequestMatch[];
  /** Customer wants loaded by a teammate: visible only inside the company, never to suppliers. */
  customer_name?: string | null;
  customer_company?: string | null;
  loaded_by_name?: string | null;
};

export type CustomerWantRow = {
  customer_name: string | null;
  customer_company: string | null;
  raw_text: string;
  product_form: string | null;
  strain_query: string | null;
  species: string | null;
  environment: string | null;
  min_quantity_g: number | null;
  max_price_per_g: number | null;
  min_thc_percent: number | null;
  needed_by: string | null;
  parsed_query: { constraints: unknown[] } | null;
};


export type NewBuyerRequest = {
  rawText: string;
  productForm?: string | null;
  strainQuery?: string | null;
  minQuantityG?: number | null;
  targetPricePerG?: number | null;
  maxPricePerG?: number | null;
  minThcPercent?: number | null;
  environment?: string | null;
  species?: string | null;
  neededBy?: string | null;
  isRecurring?: boolean;
  /** Full search-engine constraints, so matching applies every rule Search applies. */
  parsedQuery?: { constraints: unknown[] } | null;
};

const key = (uid?: string) => ['buyer-requests', uid];
const EMPTY_REQUESTS: BuyerRequest[] = [];

export function useBuyerRequests() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: key(user?.id),
    enabled: !!user,
    queryFn: async (): Promise<BuyerRequest[]> => {
      const { data, error } = await supabase.rpc('get_my_buyer_requests' as never);
      if (error) throw error;
      return (data as unknown as BuyerRequest[]) ?? [];
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: key(user?.id) });
    // Top-bar "new matches" dot reads the same match statuses.
    qc.invalidateQueries({ queryKey: ['navAttention'] });
  };

  const create = useMutation({
    mutationFn: async (r: NewBuyerRequest) => {
      const { data, error } = await supabase.rpc('create_buyer_request' as never, {
        _raw_text: r.rawText,
        _product_form: r.productForm ?? null,
        _strain_query: r.strainQuery ?? null,
        _min_quantity_g: r.minQuantityG ?? null,
        _target_price_per_g: r.targetPricePerG ?? null,
        _max_price_per_g: r.maxPricePerG ?? null,
        _min_thc_percent: r.minThcPercent ?? null,
        _environment: r.environment ?? null,
        _needed_by: r.neededBy ?? null,
        _is_recurring: r.isRecurring ?? false,
        _species: r.species ?? null,
        _parsed_query: r.parsedQuery ?? null,
      } as never);
      if (error) throw error;
      return data as unknown as { id: string; matches: number; active: number; limit: number | null };
    },
    onSuccess: invalidate,
  });

  const setStatus = useMutation({
    mutationFn: async (v: { id: string; status: 'active' | 'paused' | 'fulfilled' | 'closed' }) => {
      const { error } = await supabase.rpc('set_buyer_request_status' as never, {
        _request_id: v.id,
        _status: v.status,
      } as never);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const setMatchStatus = useMutation({
    mutationFn: async (v: { id: string; status: BuyerRequestMatch['status'] }) => {
      const { error } = await supabase.rpc('set_buyer_request_match_status' as never, {
        _match_id: v.id,
        _status: v.status,
      } as never);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /** Customer wants sheet: many reviewed wants in one call; the server scrubs customer names from supplier-visible text. */
  const bulkCreate = useMutation({
    mutationFn: async (rows: CustomerWantRow[]) => {
      const { data, error } = await supabase.rpc('bulk_create_buy_requests' as never, { _rows: rows } as never);
      if (error) throw error;
      return (data as unknown as { created: Array<{ id: string; matches: number }> }).created;
    },
    onSuccess: invalidate,
  });

  return { ...query, requests: query.data ?? EMPTY_REQUESTS, create, bulkCreate, setStatus, setMatchStatus };
}

export type PastBuyerRequest = {
  id: string;
  raw_text: string;
  product_form: string | null;
  strain_query: string | null;
  species: string | null;
  environment: string | null;
  status: 'fulfilled' | 'closed' | 'expired' | string;
  closed_at: string;
  created_at: string;
  match_count: number;
  contacted_count: number;
};

/** Finished requests (reorder rung): the buyer's stored intent, ready to make again. */
export function useBuyerRequestHistory(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['buyer-requests', user?.id, 'history'],
    enabled: !!user && enabled,
    queryFn: async (): Promise<PastBuyerRequest[]> => {
      const { data, error } = await supabase.rpc('get_my_buyer_request_history' as never);
      if (error) throw error;
      return (data as unknown as PastBuyerRequest[]) ?? [];
    },
  });
}
